// WAITS V2 — ONE WAIT PER CLIENT TURN, OVER THE SEGMENTER'S CONVERSATIONS
//
// ⭐ PURE: a channel's stored messages, its people and the segmenter's units in;
// its turns, the answered skill's user turn, the answer checked, and each wait
// decided out. No database, no clock, no model. lib/server/client-slack-
// answered.ts reads the rows, makes the calls and keeps the verdicts; the pages
// (lib/server/client-slack-response-times.ts `readWaits`) read them.
//
// A WAIT IS A CLIENT TURN in its conversation: the client messages `turnOf`
// joins (no YWS message between, no gap over 30 minutes; bots skipped over),
// E3's rule, which E4 and E9 scored with. The conversation is P3's: every unit
// (a thread, or a lone top-level message) the segmenter placed together. A unit
// it has not placed (no call made yet: the limit, the kill switch, no key, or a
// dev stack, which never runs it) is a conversation by itself — today's unit,
// and nothing is joined because of an @.
//
// WHO DECIDES, per wait:
//
//   - THE ANSWERED SKILL (`answered.v3`, after v2 and E4's `answered-c.v1`),
//     while its kept verdict was made on the turn as it stands and nothing has
//     been said in the conversation since its read (inside the window it
//     reads, below). It
//     says whether a response was owed (`needs_response`), where the clock
//     starts (`start`), and what answered it (`answered_by`: a YWS message, or
//     a YWS reaction as `<message>:<emoji>`), with a `resolution`.
//     A read that sets the turn aside (not owed) is overturned by THE SET-ASIDE
//     CHECK's yes on the same turn (lib/server/client-slack-setaside.ts;
//     `secondLookOf`): owed, answered by a YWS 👍/✅ on the turn or the rule's
//     first later YWS message.
//   - ELSE THE RULE (E4's candidate a, `ignore`): owed when any message of the
//     turn is more than an acknowledgement by the old words (`messageKind`),
//     answered by the first YWS message anywhere in the conversation after it.
//     ⚠️ NO @-MENTION IS READ, in either direction. The old rule closed a
//     lone message on a later top-level YWS message @-mentioning its author;
//     that is gone (a test pins it), and so is "a lone message is its own
//     conversation" wherever the segmenter has run.
//
// THE MODEL'S READ IS CURRENT (`current`) while the turn is the one it read and
// no message of the conversation came after `seen_upto` inside its window
// (`windowEnd`): 3 days after the turn, or, for a read that left the wait OPEN
// (`openRead`), the conversation's newest message, up to 80 after the turn. An
// open wait answered on day 5 is so stale the moment the answer lands, and the
// rule (which closes on the first later YWS message) decides it until the pass
// reads it again, on the longer window. The pass re-reads on a finer key
// (`input_hash`: the whole rendered call, so an edit, a reaction or a segmenter
// move re-reads too), but a page can't render every call on every request (the
// nav badge reads the inbox on every page), so it uses this cheap check: a
// verdict made before the newest message is the rules' until the next tick
// reads it.
//
// A REJECTED ANSWER: a person said a YWS message that closed the wait did not
// answer it ("Still needs a reply" on the popup;
// lib/server/client-slack-rejected-answers.ts keeps it). While the rejection
// stands, that message never closes the wait, whoever named it: the kept read's
// `answered_by`, or the rule's first later YWS message. The rule then looks for
// the next YWS message AFTER the latest rejected one (never an earlier one: the
// model, or the rule itself, already passed over those), so a reopened wait
// waits until someone writes again. `decideTurn` takes the wait's standing
// rejections; everything downstream (`decideWait`, the Closed tab, Response
// Times) follows from the wait's `end`, with no second rule. The answered skill
// is told (`answeredCall`'s line, so it is in the call's hash),
// `acceptAnswered` refuses a rejected message as `answered_by`, and a wait with
// a standing rejection counts as open for the pass's selection and the pages'
// staleness (`windowEnd` on the longer window).
//
// THE REACTION GATE is decided in lib/client-slack-inbox.ts `decideWait`: a YWS
// reaction on the turn closes the wait when answered names that reaction
// (`reaction` here), OR the kind is "a quick acknowledgement does" (the respond
// skill's kind) and someone at YWS reacted (`acked`). Neither half counts a 👀
// ("seen" answers nothing), so `acked`, `reaction` and `ackEmoji` here never
// come from one.
//
// Erasable TypeScript: the client-slack-sync sidecar imports this under node's
// strip-only loader.

import { conversationTurns, renderAsRead, roster } from "@/lib/client-slack-answered";
import { REACTION, RESOLUTIONS, stripTone, type EvalMessage, type EvalPerson, type Side } from "@/lib/client-slack-eval";
import type { PromptMessage, PromptPerson } from "@/lib/client-slack-prompts";
import type { SegmentMessage } from "@/lib/client-slack-segments";
import { messageKind, spoken, type Wait } from "@/lib/client-slack-waits";
import { waitId, waitInputHash } from "@/lib/server/client-slack-wait-key";

/** How long after a turn the skill reads its conversation (E4: re-read on each move, for 3 days), unless its read left the wait open. */
export const ANSWERED_HORIZON_S = 3 * 86_400;
/**
 * An open wait's window runs to the conversation's newest message, but never
 * past this many messages after the turn: the oldest, where an answer is.
 * It never shows less than the 3 days.
 */
export const OPEN_AFTER_MAX = 80;
/** How much of the conversation before the turn a call shows: the labelled set's 7 days (E1). */
export const ANSWERED_BEFORE_S = 7 * 86_400;
/** The resolutions that end a wait with no YWS answer: nothing is owed any more, and nothing can be timed. */
export const SETTLED = ["moved_off_slack", "superseded", "client_self_answered"] as const;
export type Settled = (typeof SETTLED)[number];
export type Resolution = (typeof RESOLUTIONS)[number];

const secs = (ts: string): number => Number(ts);
const byTs = (a: { ts: string }, b: { ts: string }): number => secs(a.ts) - secs(b.ts) || (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0);
/** A message's unit: its thread's root ts, or its own. */
export const unitOf = (m: { ts: string; threadTs: string | null }): string => m.threadTs ?? m.ts;

/** Who wrote a message, as the skill's guide tags them. */
export function sideOfMessage(m: PromptMessage, people: ReadonlyMap<string, PromptPerson>): Side {
  if (m.botId !== null) return "bot";
  const p = m.userId === null ? undefined : people.get(m.userId);
  if (p?.bot) return "bot";
  return p?.yws ? "yws" : "client";
}

/**
 * The messages a person reads in the channel, as the segmenter shows them
 * (lib/client-slack-segment.ts): people talking, and bots' posts as bots; not
 * Slack's notices.
 */
export const shownMessage = (m: PromptMessage, people: ReadonlyMap<string, PromptPerson>): boolean =>
  spoken(m, people) || (m.botId !== null && m.subtype !== "channel_join");

// ─── Conversations and turns ─────────────────────────────────────────────────

export interface TurnConversation {
  /** `c<id>` for a stored conversation, `u<unit ts>` for an unplaced unit read alone. */
  key: string;
  /** client_slack_conversation.id, or null for an unplaced unit. */
  id: number | null;
  /** Its shown messages, oldest first. */
  messages: PromptMessage[];
}

export interface Turn {
  channelId: string;
  conversation: TurnConversation;
  /** The turn's messages' ts, oldest first. */
  anchors: string[];
  /** The unit of its first message: the wait id's thread, and where a reply from the inbox goes. */
  threadTs: string;
}

/**
 * A channel's conversations: the stored ones by their units (`unitConv`, unit
 * ts → conversation id), and every unit no conversation holds as one by
 * itself. `messages` in any order, deleted ones already left out.
 */
export function channelTurnConversations(messages: readonly PromptMessage[], people: ReadonlyMap<string, PromptPerson>, unitConv: ReadonlyMap<string, number>): TurnConversation[] {
  const out = new Map<string, TurnConversation>();
  for (const m of messages.filter((x) => shownMessage(x, people)).sort(byTs)) {
    const id = unitConv.get(unitOf(m)) ?? null;
    const key = id === null ? `u${unitOf(m)}` : `c${id}`;
    const c = out.get(key) ?? { key, id, messages: [] };
    c.messages.push(m);
    out.set(key, c);
  }
  return Array.from(out.values());
}

/**
 * Every client turn in a channel whose first message is at or after `since`
 * (seconds): an older one is context only, as the old rule's. Read `since`'s
 * caller a few days earlier than that, so a turn on the edge is the same turn
 * on every read.
 */
export function channelTurns(channelId: string, messages: readonly PromptMessage[], people: ReadonlyMap<string, PromptPerson>, unitConv: ReadonlyMap<string, number>, since: number): Turn[] {
  const out: Turn[] = [];
  for (const conversation of channelTurnConversations(messages, people, unitConv)) {
    const ms = conversation.messages;
    const seg: SegmentMessage[] = ms.map((m, k) => ({ id: `m${k + 1}`, ts: m.ts, thread: null, side: sideOfMessage(m, people) }));
    const tsOf = new Map(seg.map((s) => [s.id, s.ts]));
    for (const ids of conversationTurns(seg, seg.map((s) => s.id))) {
      const anchors = ids.map((id) => tsOf.get(id) as string);
      if (secs(anchors[0]) < since) continue;
      const first = ms.find((m) => m.ts === anchors[0]) as PromptMessage;
      out.push({ channelId, conversation, anchors, threadTs: unitOf(first) });
    }
  }
  return out.sort((a, b) => secs(a.anchors[0]) - secs(b.anchors[0]));
}

// ─── The call ─────────────────────────────────────────────────────────────────

/** The answer's shape, as the E9/E10 per-turn runner asked for it (tools/client-slack-eval-per-conversation.ts `TURN_SHAPE`). */
export const ANSWERED_SHAPE = `{"batch": "<name>", "labels": [{"id": "<wait id>", "needs_response": true|false, "start": "<id>"|null, "answered_by": "<id>"|"<id>:<emoji>"|null, "resolution": "<enum>", "note": "<one sentence>"}]}`;

/** What one call shows and how its ids map back. */
export interface AnsweredCall {
  waitId: string;
  /** The user turn, exactly as sent. */
  text: string;
  /** The turn's ids in the call (`m<k>`), oldest first. */
  turnIds: string[];
  /** The call's id → its message. */
  byId: Map<string, { m: PromptMessage; side: Side }>;
  /** The newest message the call shows: a later one inside its window (`windowEnd`) makes the verdict stale for the pages. */
  seenUpto: string;
  /** The call's ids of the messages a person rejected as this wait's answer: `acceptAnswered` refuses them. */
  rejected: string[];
}

/**
 * Where a turn's window ends (seconds, inclusive): 3 days after the turn's last
 * message; for an `open` turn (its kept read left it open, `openRead`), the
 * conversation's newest message, or the `OPEN_AFTER_MAX`th message after the
 * turn when there are more, and never sooner than the 3 days. The call
 * (`callWindow`) and the pages' staleness check (`current`) both end here, so
 * a read is stale exactly when its call would show something new.
 */
export function windowEnd(turn: Turn, open: boolean): number {
  const last = secs(turn.anchors[turn.anchors.length - 1]);
  const days = last + ANSWERED_HORIZON_S;
  if (!open) return days;
  const after = turn.conversation.messages.filter((m) => secs(m.ts) > last);
  return after.length <= OPEN_AFTER_MAX ? Infinity : Math.max(days, secs(after[OPEN_AFTER_MAX - 1].ts));
}

/**
 * The messages of a turn's conversation one call shows: everything up to its
 * window's end (`windowEnd`: 3 days after the turn, longer for an open one),
 * from 7 days before it (E1's window), and every message of a thread that
 * window touches (a root older than the 7 days comes with its thread, as E1's
 * windows did). The respond skill's call shows the 3 days (`open` false), as
 * E5 measured it.
 */
export function callWindow(turn: Turn, open = false): PromptMessage[] {
  const first = secs(turn.anchors[0]);
  const upTo = windowEnd(turn, open);
  const ms = turn.conversation.messages.filter((m) => secs(m.ts) <= upTo);
  const touched = new Set(ms.filter((m) => secs(m.ts) >= first - ANSWERED_BEFORE_S).map(unitOf));
  return ms.filter((m) => touched.has(unitOf(m)));
}

/** A turn's window drawn as the skills read it (E1's rendering), ids `m1…` per call in time order. */
export interface RenderedTurn {
  /** The turn's ids in the call (`m<k>`), oldest first. */
  turnIds: string[];
  /** The call's id → its message. */
  byId: Map<string, { m: PromptMessage; side: Side }>;
  /** A message's ts → its id in the call. */
  idOf: Map<string, string>;
  /** `People: …`, then the messages fenced, one line each, and a blank. */
  body: string[];
  /** The newest message drawn. */
  seenUpto: string;
}

/**
 * One turn's window (`callWindow`) drawn as E4's per-wait block draws it, the
 * ids numbered from m1 in time order per call (as P3's chunk). The answered
 * skill (`answeredCall`) and the respond skill (lib/client-slack-respond.ts
 * `respondCall`) put their own header and wait line above it.
 */
export function renderTurn(turn: Turn, people: ReadonlyMap<string, PromptPerson>, open = false): RenderedTurn {
  const r = renderMessages(callWindow(turn, open), people);
  return { ...r, turnIds: turn.anchors.map((ts) => r.idOf.get(ts) as string) };
}

/**
 * Some messages of one conversation, oldest first, drawn as E4's per-wait
 * block draws them (the People line, then the messages fenced, one line each),
 * ids numbered from m1 in time order. `renderTurn` draws a turn's window with
 * it; the action items' call (lib/client-slack-items-v3.ts) draws a whole
 * conversation, as E6's list read did.
 */
export function renderMessages(ms: readonly PromptMessage[], people: ReadonlyMap<string, PromptPerson>): Omit<RenderedTurn, "turnIds"> {
  const { evals, named, byId, idOf } = asEvalMessages(ms, people);
  const body = [
    `People: ${roster(evals, named)
      .map((p) => `${p.name} [${p.side === "yws" ? "YWS" : p.side}]`)
      .join(", ")}`,
    "```",
    ...evals.map((e) => renderAsRead(e, named, true)),
    "```",
    "",
  ];
  return { byId, idOf, body, seenUpto: ms[ms.length - 1].ts };
}

/**
 * Some messages of one conversation as the eval's messages (`EvalMessage`),
 * ids numbered from m1 in time order, with the people by their side: what
 * `renderMessages` draws, and what the set-aside check draws its own way
 * (lib/client-slack-setaside.ts `setasideUser`).
 */
export function asEvalMessages(
  ms: readonly PromptMessage[],
  people: ReadonlyMap<string, PromptPerson>,
): { evals: EvalMessage[]; named: Record<string, EvalPerson>; byId: Map<string, { m: PromptMessage; side: Side }>; idOf: Map<string, string> } {
  const idOf = new Map(ms.map((m, k) => [m.ts, `m${k + 1}`]));
  const replies = new Map<string, number>();
  for (const m of ms) if (m.threadTs !== null && m.threadTs !== m.ts && idOf.has(m.threadTs)) replies.set(m.threadTs, (replies.get(m.threadTs) ?? 0) + 1);
  const named: Record<string, EvalPerson> = {};
  for (const [u, p] of people) named[u] = { name: p.name ?? "Unknown person", side: p.bot ? "bot" : p.yws ? "yws" : "client" };
  const byId = new Map<string, { m: PromptMessage; side: Side }>();
  const evals: EvalMessage[] = ms.map((m) => {
    const self = idOf.get(m.ts) as string;
    byId.set(self, { m, side: sideOfMessage(m, people) });
    const root = m.threadTs !== null && m.threadTs !== m.ts ? (idOf.get(m.threadTs) ?? null) : null;
    const n = replies.get(m.ts) ?? 0;
    return {
      id: self,
      ts: m.ts,
      // A reply whose root isn't shown reads as top-level, as P3's chunk reads it.
      thread: root ?? (n > 0 ? self : null),
      replies: root === null ? n : 0,
      user: m.userId,
      bot: m.botId !== null || (m.userId !== null && (people.get(m.userId)?.bot ?? false)),
      text: m.text ?? "",
      files: (m.files ?? []).map((f) => f.name ?? ""),
      reactions: (m.reactions ?? []).map((r) => ({ name: r.name, users: [...(r.users ?? [])] })),
    };
  });
  return { evals, named, byId, idOf };
}

/** No rejected answers. */
const NONE: ReadonlySet<string> = new Set();

/**
 * The answered skill's user turn for one wait: E4's per-wait block with E9's
 * one-item header (the shape E7, E9 and E10 sent through OpenRouter), the ids
 * numbered from m1 in time order per call (as P3's chunk). `open`: its kept
 * read left the wait open, or a person rejected an answer, so the window runs
 * on past the 3 days (`windowEnd`).
 *
 * `rejected`: the ts of the messages a person said did not answer this turn.
 * Those the call shows are named in one line under the wait's heading ("YWS
 * says m14 did not answer this turn."), so the line is in the call's hash; none
 * shown, no line, and the call is byte for byte what it was before rejections
 * (every kept hash stands).
 */
export function answeredCall(turn: Turn, people: ReadonlyMap<string, PromptPerson>, id: string = waitId(turn), open = false, rejected: ReadonlySet<string> = NONE): AnsweredCall {
  const r = renderTurn(turn, people, open);
  const no = [...r.idOf].filter(([ts]) => rejected.has(ts)).map(([, k]) => k);
  const text = [
    `# Batch ${id} (1 wait)`,
    "",
    "Answer with ONE JSON object, and nothing else, with exactly this shape:",
    "",
    "```",
    ANSWERED_SHAPE,
    "```",
    "",
    `### Wait ${id} — target ${r.turnIds[0]}; the client's turn: ${r.turnIds.join(", ")}`,
    ...(no.length === 0 ? [] : [`YWS says ${no.length === 1 ? no[0] : `${no.slice(0, -1).join(", ")} and ${no[no.length - 1]}`} did not answer this turn.`]),
    ...r.body,
  ].join("\n");
  return { waitId: id, text, turnIds: r.turnIds, byId: r.byId, seenUpto: r.seenUpto, rejected: no };
}

// ─── The answer ──────────────────────────────────────────────────────────────

/** Why an answer was refused. The call's receipt says `invalid_schema`. */
export class AnsweredRefused extends Error {}

/** An accepted answer, in Slack's terms. */
export interface AnsweredVerdict {
  needs: boolean;
  /** The ts of the turn's first message that needed a response, or null. */
  start: string | null;
  /** `<ts>` for a YWS message, `<ts>:<emoji>` for a YWS reaction (skin tone stripped), or null. */
  answeredBy: string | null;
  resolution: Resolution;
  note: string;
}

const NOTE_MAX = 300;
const toneless = (name: string): string => stripTone(name) as string;

/**
 * The model's answer checked against the call it answered, or refused: E4's
 * `checkAnswer` (the fields agree with each other; ids are the call's), and the
 * two guards — no answer before the turn, and no client message (nor a
 * bot's) as an answer. A reaction answers only from a message of the turn,
 * and only a YWS person's reaction of that emoji (E1: a reaction carries no
 * time, so it must be one the turn really has). Skin tone is stripped first
 * (`+1::skin-tone-4` → `+1`; Sonnet wrote it on 3 of 250 scored waits, E9b).
 * Accepts the `{batch, labels: [...]}` it was asked for, or a bare label.
 */
export function acceptAnswered(value: unknown, call: Pick<AnsweredCall, "waitId" | "turnIds" | "byId"> & { rejected?: readonly string[] }, people: ReadonlyMap<string, PromptPerson>): AnsweredVerdict {
  const v = value as { labels?: unknown; needs_response?: unknown } | null;
  if (v === null || typeof v !== "object") throw new AnsweredRefused("not an object");
  let label: unknown = v;
  if (Array.isArray(v.labels)) {
    const ls = v.labels as Array<{ id?: unknown }>;
    label = ls.find((l) => l !== null && typeof l === "object" && l.id === call.waitId) ?? (ls.length === 1 ? ls[0] : undefined);
  }
  const l = label as Record<string, unknown> | undefined;
  if (l === undefined || l === null || typeof l !== "object") throw new AnsweredRefused("no label for this wait");
  const bad: string[] = [];
  if (typeof l.needs_response !== "boolean") bad.push("needs_response: not a boolean");
  if (typeof l.resolution !== "string" || !(RESOLUTIONS as readonly string[]).includes(l.resolution)) bad.push("resolution: not one of the list");
  const needs = l.needs_response === true;
  if (needs) {
    if (typeof l.start !== "string" || !call.turnIds.includes(l.start)) bad.push("start: not one of the turn's ids");
    if (l.resolution === "not_needed") bad.push("resolution: not_needed though a response was needed");
  } else if (l.needs_response === false) {
    if (l.start !== null && l.start !== undefined) bad.push("start: not null when no response is needed");
    if (l.answered_by !== null && l.answered_by !== undefined) bad.push("answered_by: not null when no response is needed");
    if (l.resolution !== "not_needed") bad.push("resolution: not not_needed when no response is needed");
  }
  let answeredBy: string | null = null;
  if (l.answered_by === undefined) bad.push("answered_by: missing");
  else if (l.answered_by !== null) {
    if (typeof l.answered_by !== "string") bad.push("answered_by: not an id, <id>:<emoji> or null");
    else {
      const raw = toneless(l.answered_by);
      const r = REACTION.exec(raw);
      const id = r ? r[1] : raw;
      const hit = call.byId.get(id);
      const lastTurn = call.byId.get(call.turnIds[call.turnIds.length - 1]) as { m: PromptMessage };
      if (!hit) bad.push(`answered_by: ${id} is not a message of this call`);
      else if (r) {
        const emoji = toneless(r[2]);
        const ours = (hit.m.reactions ?? []).some((x) => toneless(x.name) === emoji && (x.users ?? []).some((u) => (people.get(u)?.yws ?? false) && !(people.get(u)?.bot ?? false)));
        if (!call.turnIds.includes(id)) bad.push("answered_by: a reaction answers only from a message of the turn");
        else if (!ours) bad.push(`answered_by: nobody at YWS reacted :${emoji}: there`);
        else answeredBy = `${hit.m.ts}:${emoji}`;
      } else if (hit.side !== "yws") bad.push(`answered_by: ${id} is a ${hit.side} message, not YWS's`);
      else if (byTs(hit.m, lastTurn.m) <= 0) bad.push(`answered_by: ${id} comes before the turn ends`);
      else if ((call.rejected ?? []).includes(id)) bad.push(`answered_by: YWS says ${id} did not answer this turn`);
      else answeredBy = hit.m.ts;
    }
  }
  if (bad.length > 0) throw new AnsweredRefused(bad.join("; "));
  const startTs = needs ? (call.byId.get(l.start as string) as { m: PromptMessage }).m.ts : null;
  const note = typeof l.note === "string" ? l.note.trim().slice(0, NOTE_MAX) : "";
  return { needs, start: startTs, answeredBy, resolution: l.resolution as Resolution, note };
}

// ─── Deciding a wait ─────────────────────────────────────────────────────────

/** A kept verdict (`client_slack_wait`), as the pages read it. */
export interface KeptAnswer extends AnsweredVerdict {
  waitId: string;
  /** The turn it read, its messages' ts. */
  turn: string[];
  /** The newest message its call showed. */
  seenUpto: string;
  model: string;
  at: number;
  /**
   * The set-aside check's yes on this turn
   * (lib/server/client-slack-setaside.ts), or absent: the turn it read, and
   * what YWS must now do. Used only on a read that set the turn aside, for the
   * same turn (`secondLookOf`).
   */
  secondLook?: { turn: string[]; what: string } | null;
}

/** A wait, decided: the old `Wait`'s fields (so the triage, the inbox and Response Times read it as before), and who decided. */
export interface WaitV2 extends Omit<Wait, "endedBy"> {
  /** The old rule's `thread` / `mention` never: the message the answered skill named (`model`), or the rule's first YWS message in the conversation (`conversation`). */
  endedBy: "model" | "conversation" | null;
  id: string;
  /** The turn's words hashed (`waitInputHash`): what a person's mark and the triage's read are kept on. */
  inputHash: string;
  /** The conversation it is in (client_slack_conversation.id), or null for an unplaced unit. */
  conversationId: number | null;
  /** Who decided `needs`, `start` and `end`: the answered skill's current read, or the rule. */
  by: "model" | "rules";
  /** The answered skill named a YWS reaction as its answer: the reaction gate's half (E7). */
  reaction: boolean;
  /**
   * The emoji a reaction closing it would be (the popup's "Acknowledged" tag),
   * skin tone stripped: the one the answered skill named, else the first
   * YWS reaction on the turn's latest message to carry one; null for none.
   */
  ackEmoji: string | null;
  /**
   * The message that answered it: the one the answered skill named, or the
   * rule's first later YWS message; null while `end` is. The popup draws it
   * wherever it sits in the conversation.
   */
  answerTs: string | null;
  /** The turn's message the `ackEmoji` reaction sits on, or null: what the popup shows when a reaction closed it. */
  ackTs: string | null;
  /** Settled with no YWS answer (the skill's moved off Slack, superseded, or the client answered themselves), or null. */
  settled: Settled | null;
  /** The skill's one sentence on its hardest call, or null. */
  note: string | null;
  /**
   * When the answered skill's current read was made (ms), or null where the
   * rule decided: the Closed tab's order for a close Slack keeps no time for, a
   * reaction or a settled read.
   */
  readAt: number | null;
  /**
   * The set-aside check's line when it put back a wait the answered skill set
   * aside as finished: what YWS must now do. Null for every other wait.
   */
  secondLook: string | null;
}

/** Slack's 👀: "seen", never an answer. */
export const SEEN_EMOJI = "eyes";

/** A YWS reaction of this emoji can close a wait: anything but 👀 (D4). Skin tone ignored. */
export const answersAsReaction = (name: string): boolean => toneless(name) !== SEEN_EMOJI;

const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * A kept read left the wait open: owed, nothing named as its answer, and not
 * settled. Such a read is read again whenever its conversation moves, at any
 * age, on the longer window; any other read stands once the turn is 3 days
 * old (lib/server/client-slack-answered.ts `runAnswered`).
 *
 * A read that named only a 👀 left it open too: `decideTurn` closes nothing on
 * it, so it must not stand forever as the answer it isn't. answered.v1 kept 20
 * such reads on the 2026-10-18 snapshot, all past their 3 days: like any open
 * read, each is read again when its conversation moves (and once on
 * answered.v2's first tick, whose version is in the hash).
 */
export const openRead = (kept: Pick<AnsweredVerdict, "needs" | "answeredBy" | "resolution">): boolean =>
  kept.needs && (kept.answeredBy === null ? kept.resolution === "open" : namesOnlySeen(kept.answeredBy));

/** `<ts>:eyes`: the answer named is a 👀, which answers nothing (D4). */
const namesOnlySeen = (answeredBy: string): boolean => {
  const emoji = /^\d+\.\d+:(.+)$/.exec(answeredBy)?.[1];
  return emoji !== undefined && !answersAsReaction(emoji);
};

/**
 * A kept verdict still speaks for the turn: the same turn, nothing said in
 * the conversation after what it read (inside its window, `windowEnd`: the 3
 * days, or on to the newest message for an open read, or for a wait a person
 * reopened, `reopened`), and the messages it names still there. See the
 * header.
 */
export function current(kept: KeptAnswer | undefined, turn: Turn, reopened = false): kept is KeptAnswer {
  if (kept === undefined || !sameList(kept.turn, turn.anchors)) return false;
  const upTo = windowEnd(turn, openRead(kept) || reopened);
  const seen = secs(kept.seenUpto);
  if (turn.conversation.messages.some((m) => secs(m.ts) > seen && secs(m.ts) <= upTo)) return false;
  if (kept.start !== null && !turn.anchors.includes(kept.start)) return false;
  const by = kept.answeredBy === null ? null : (/^(\d+\.\d+)(?::|$)/.exec(kept.answeredBy)?.[1] ?? kept.answeredBy);
  return by === null || turn.conversation.messages.some((m) => m.ts === by);
}

/**
 * The set-aside check's yes that overturns a kept read, or null: the read set
 * the turn aside (not owed) and the check said yes on this same turn. The read
 * must be current; the caller asks that.
 */
export function secondLookOf(kept: KeptAnswer, turn: Turn): string | null {
  const s = kept.secondLook;
  return !kept.needs && s !== undefined && s !== null && sameList(s.turn, turn.anchors) ? s.what : null;
}

/** 👍 or ✅ (skin tone stripped): "will do", the reactions that answer a turn the check put back (D4). */
const WILL_DO = new Set(["+1", "thumbsup", "white_check_mark", "heavy_check_mark"]);

/**
 * One turn decided: by the answered skill's current read, else by the rule
 * (see the header). A read that set the turn aside is overturned by the
 * set-aside check's yes (`secondLookOf`): owed from the turn's first
 * message that is more than an acknowledgement, answered by a YWS 👍 or ✅
 * on it (D4: "will do"), else by the rule's first later YWS message. The returned wait is the old `Wait` with `by`, the
 * reaction gate's half and how it settled.
 *
 * `rejected`: the ts of the YWS messages a person said did not answer this
 * wait, while the rejections stand. None of them ends it: a kept read naming
 * one keeps its `needs` and `start` but not its answer, and the rule's answer
 * is the first YWS message after the latest of them (see the header).
 */
export function decideTurn(turn: Turn, people: ReadonlyMap<string, PromptPerson>, kept: KeptAnswer | undefined, rejected: ReadonlySet<string> = NONE): WaitV2 {
  const ms = turn.conversation.messages;
  const inTurn = new Set(turn.anchors);
  const turnMs = ms.filter((m) => inTurn.has(m.ts));
  const yws = (u: string | null): boolean => u !== null && (people.get(u)?.yws ?? false) && !(people.get(u)?.bot ?? false);
  const authors: string[] = [];
  for (const m of turnMs) if (m.userId !== null && !authors.includes(m.userId)) authors.push(m.userId);
  const kinds = turnMs.map((m) => messageKind(m.text));
  const base = {
    channelId: turn.channelId,
    threadTs: turn.threadTs,
    anchors: turn.anchors,
    authors,
    kinds,
    // 👀 is "seen" and answers nothing (D4), so it never closes a wait.
    acked: turnMs.some((m) => (m.reactions ?? []).some((r) => answersAsReaction(r.name) && (r.users ?? []).some(yws))),
    id: waitId(turn),
    inputHash: waitInputHash(turn, turnMs),
    conversationId: turn.conversation.id,
  };
  const last = turnMs[turnMs.length - 1];
  // The guide's own convention for a reaction answer: the latest message of the
  // turn carrying a YWS reaction, its first one. 👀 left out: a closed
  // wait's tag must never read "Acknowledged 👀".
  const ywsReaction = turnMs
    .map((m) => ({ ts: m.ts, name: (m.reactions ?? []).find((r) => answersAsReaction(r.name) && (r.users ?? []).some(yws))?.name }))
    .filter((x): x is { ts: string; name: string } => x.name !== undefined)
    .at(-1);
  const ackedWith = ywsReaction === undefined ? null : toneless(ywsReaction.name);
  const ackedOn = ywsReaction?.ts ?? null;
  // The rule's answer (E4's a, `ignore`): no @ read anywhere. Past every
  // rejected answer: the next YWS message after the latest of them.
  const needy = turnMs.filter((_, i) => kinds[i] !== "ack");
  const reopened = rejected.size > 0;
  const floor = Array.from(rejected, (ts) => ({ ts })).reduce<{ ts: string }>((a, b) => (byTs(b, a) > 0 ? b : a), last);
  const after = ms.find((m) => byTs(m, floor) > 0 && !inTurn.has(m.ts) && spoken(m, people) && yws(m.userId)) ?? null;
  const isCurrent = current(kept, turn, reopened);
  const second = isCurrent ? secondLookOf(kept, turn) : null;
  if (second !== null) {
    const willDo = turnMs
      .map((m) => ({ ts: m.ts, name: (m.reactions ?? []).find((r) => WILL_DO.has(toneless(r.name)) && (r.users ?? []).some(yws))?.name }))
      .filter((x): x is { ts: string; name: string } => x.name !== undefined)
      .at(-1);
    const end = willDo === undefined ? after : null;
    return {
      ...base,
      start: secs((needy[0] ?? turnMs[0]).ts),
      end: end === null ? null : secs(end.ts),
      answeredBy: end?.userId ?? null,
      endedBy: end === null ? null : "conversation",
      needs: true,
      by: "model",
      reaction: willDo !== undefined,
      ackEmoji: willDo !== undefined ? toneless(willDo.name) : ackedWith,
      answerTs: end?.ts ?? null,
      ackTs: willDo?.ts ?? ackedOn,
      settled: null,
      note: (kept as KeptAnswer).note || null,
      readAt: (kept as KeptAnswer).at,
      secondLook: second,
    };
  }
  if (isCurrent) {
    const answer = kept.answeredBy === null ? null : /^(\d+\.\d+)(?::(.+))?$/.exec(kept.answeredBy);
    const named = answer !== null && answer[2] === undefined ? (ms.find((m) => m.ts === answer[1]) ?? null) : null;
    // A person rejected the message it named: the read keeps its "owed"
    // and its start, and the rule's next answer past the rejection, if any, ends it.
    const refused = named !== null && rejected.has(named.ts);
    const msg = refused ? after : named;
    // A 👀 the skill named is no answer either (D4): the wait stays as if it named none.
    const reaction = answer !== null && answer[2] !== undefined && answersAsReaction(answer[2]);
    const settled = kept.needs && msg === null && !reaction && (SETTLED as readonly string[]).includes(kept.resolution) ? (kept.resolution as Settled) : null;
    return {
      ...base,
      start: secs(kept.start ?? turn.anchors[0]),
      end: msg === null ? null : secs(msg.ts),
      answeredBy: msg?.userId ?? null,
      endedBy: msg === null ? null : refused ? "conversation" : "model",
      needs: kept.needs,
      by: "model",
      reaction: kept.needs && reaction,
      ackEmoji: kept.needs && reaction ? toneless(answer?.[2] as string) : ackedWith,
      answerTs: msg?.ts ?? null,
      ackTs: kept.needs && reaction ? (answer?.[1] ?? null) : ackedOn,
      settled,
      note: kept.note || null,
      readAt: kept.at,
      secondLook: null,
    };
  }
  return {
    ...base,
    start: secs((needy[0] ?? turnMs[0]).ts),
    end: after === null ? null : secs(after.ts),
    answeredBy: after?.userId ?? null,
    endedBy: after === null ? null : "conversation",
    needs: needy.length > 0,
    by: "rules",
    reaction: false,
    ackEmoji: ackedWith,
    answerTs: after?.ts ?? null,
    ackTs: ackedOn,
    settled: null,
    note: null,
    readAt: null,
    secondLook: null,
  };
}
